import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useMsal } from "@azure/msal-react";
import { RefreshCw, Save, Plus, Trash2, Edit2, DatabaseZap } from "lucide-react";
import axios from "axios";
import { apiRequest } from "../authConfig";
import {
  BatchColorSetting,
  getBatchColorSettings,
  updateBatchColorSetting,
  createBatchColor,
  deleteBatchColor,
} from "../services/batchColorApiService";
import {
  EntityDto,
  createEntity,
  deleteEntity,
  getEntities,
  updateEntity,
} from "../services/entityApiService";
import { IconPicker, DynamicIcon } from "../components/IconPicker";
import NotificationEmailsAdmin from "../components/NotificationEmailsAdmin";

const SYNC_URL = process.env.REACT_APP_ACUSYNC_URL;

const AdminToolsPage: React.FC = () => {
  const { isAdmin } = useUser();
  const { instance, accounts } = useMsal();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheResult, setCacheResult] = useState<{ success: boolean; message: string } | null>(null);

  const [colors, setColors] = useState<BatchColorSetting[]>([]);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [savingColor, setSavingColor] = useState<string | null>(null);
  const [colorSaveMsg, setColorSaveMsg] = useState<string | null>(null);

  // New color form
  const [newLabel, setNewLabel] = useState("");
  const [newHex, setNewHex] = useState("#3b82f6");
  const [newIcon, setNewIcon] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Entities state
  const [entities, setEntities] = useState<EntityDto[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entityEditingId, setEntityEditingId] = useState<number | null>(null);
  const [entityName, setEntityName] = useState("");
  const [entityBranchesText, setEntityBranchesText] = useState("");
  const [entityMsg, setEntityMsg] = useState<string | null>(null);
  const [entityUseMaterialCount, setEntityUseMaterialCount] = useState(true);
  const [entityUseMaterialQty,   setEntityUseMaterialQty]   = useState(true);
  const [entityUseSupplier,      setEntityUseSupplier]      = useState(true);
  const [entityUseHauler,        setEntityUseHauler]        = useState(true);
  const [entityUseBatchPageV2,   setEntityUseBatchPageV2]   = useState(false);

  const refreshColors = async () => {
    if (accounts.length === 0) return;
    setColorsLoading(true);
    try {
      setColors(await getBatchColorSettings(instance, accounts[0]));
    } catch {
      setColorSaveMsg("Failed to load color settings.");
    } finally {
      setColorsLoading(false);
    }
  };

  const refreshEntities = async () => {
    if (accounts.length === 0) return;
    setEntitiesLoading(true);
    try {
      setEntities(await getEntities(instance, accounts[0]));
    } catch {
      setEntityMsg("Failed to load entities.");
    } finally {
      setEntitiesLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin || accounts.length === 0) return;
    refreshColors();
    refreshEntities();
    // eslint-disable-next-line
  }, [isAdmin, instance, accounts]);

  const resetEntityForm = () => {
    setEntityEditingId(null);
    setEntityName("");
    setEntityBranchesText("");
    setEntityUseMaterialCount(true);
    setEntityUseMaterialQty(true);
    setEntityUseSupplier(true);
    setEntityUseHauler(true);
    setEntityUseBatchPageV2(false);
  };

  const startEditEntity = (e: EntityDto) => {
    setEntityEditingId(e.id);
    setEntityName(e.name);
    setEntityBranchesText(e.branches.join(", "));
    setEntityUseMaterialCount(e.useMaterialCount);
    setEntityUseMaterialQty(e.useMaterialQty);
    setEntityUseSupplier(e.useSupplier);
    setEntityUseHauler(e.useHauler);
    setEntityUseBatchPageV2(e.useBatchPageV2);
  };

  const saveEntity = async () => {
    const name = entityName.trim();
    if (!name) return;
    const branches = entityBranchesText
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setEntityMsg(null);
    const payload = {
      name, branches,
      useMaterialCount: entityUseMaterialCount,
      useMaterialQty:   entityUseMaterialQty,
      useSupplier:      entityUseSupplier,
      useHauler:        entityUseHauler,
      useBatchPageV2:   entityUseBatchPageV2,
    };
    try {
      if (entityEditingId == null) {
        await createEntity(instance, accounts[0], payload);
      } else {
        await updateEntity(instance, accounts[0], entityEditingId, payload);
      }
      resetEntityForm();
      await refreshEntities();
    } catch {
      setEntityMsg("Failed to save entity.");
    }
  };

  const removeEntity = async (id: number) => {
    if (!window.confirm("Delete this entity? Users assigned to it will be unassigned.")) return;
    try {
      await deleteEntity(instance, accounts[0], id);
      if (entityEditingId === id) resetEntityForm();
      await refreshEntities();
    } catch {
      setEntityMsg("Failed to delete entity.");
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Access Denied</h1>
        <p className="text-slate-500 mt-2">You do not have permission to view this page.</p>
        <Link to="/" className="text-primary hover:underline mt-4 block">Go to Dashboard</Link>
      </div>
    );
  }

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      await axios.post(`${SYNC_URL}`);
      try {
        const tokenResp = await instance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/tickets/metadata/clear-cache`,
          null,
          { headers: { Authorization: `Bearer ${tokenResp.accessToken}` } },
        );
        setResult({ success: true, message: "Sync completed successfully. Cache cleared." });
      } catch (cacheErr) {
        console.error("Cache clear failed:", cacheErr);
        setResult({ success: true, message: "Sync completed, but failed to clear cache." });
      }
    } catch (error: any) {
      console.error("Sync failed:", error);
      setResult({ success: false, message: "Sync failed. Check the console for details." });
    } finally {
      setSyncing(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setCacheResult(null);
    try {
      const tokenResp = await instance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/metadata/clear-cache`,
        null,
        { headers: { Authorization: `Bearer ${tokenResp.accessToken}` } },
      );
      setCacheResult({ success: true, message: "Server cache cleared successfully." });
    } catch (error: any) {
      console.error("Cache clear failed:", error);
      setCacheResult({ success: false, message: "Failed to clear cache. Check the console for details." });
    } finally {
      setClearingCache(false);
    }
  };

  const updateLocalColor = (value: string, patch: Partial<BatchColorSetting>) => {
    setColors((prev) => prev.map((c) => (c.colorValue === value ? { ...c, ...patch } : c)));
  };

  const saveColor = async (color: BatchColorSetting) => {
    setSavingColor(color.colorValue);
    setColorSaveMsg(null);
    try {
      await updateBatchColorSetting(instance, accounts[0], color.colorValue, {
        label: color.label,
        isEnabled: color.isEnabled,
        hexColor: color.hexColor,
        icon: color.icon,
      });
      setColorSaveMsg(`Saved "${color.label}".`);
    } catch {
      setColorSaveMsg(`Failed to save "${color.colorValue}".`);
    } finally {
      setSavingColor(null);
    }
  };

  const handleCreateColor = async () => {
    if (!newLabel.trim() || !newHex.trim()) return;
    setCreating(true);
    setColorSaveMsg(null);
    try {
      await createBatchColor(instance, accounts[0], {
        label: newLabel.trim(),
        hexColor: newHex,
        icon: newIcon,
      });
      setNewLabel("");
      setNewHex("#3b82f6");
      setNewIcon(null);
      await refreshColors();
      setColorSaveMsg("Color created.");
    } catch {
      setColorSaveMsg("Failed to create color.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteColor = async (colorValue: string) => {
    if (!window.confirm("Delete this color tag? Batches using it will keep their tag but it won't render.")) return;
    setColorSaveMsg(null);
    try {
      await deleteBatchColor(instance, accounts[0], colorValue);
      await refreshColors();
      setColorSaveMsg("Color deleted.");
    } catch {
      setColorSaveMsg("Failed to delete color.");
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-background-dark p-8">
      <div className="max-w-3xl mx-auto space-y-8">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Admin Tools</h1>

        {/* Acumatica Sync */}
        <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Acumatica Data Sync</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Pulls the latest data from Acumatica into the local database. This syncs vendor locations, blanket purchase orders, and financial periods. It runs automatically every night at midnight, but you can trigger it manually here if data is out of date.
              </p>
            </div>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Run Sync"}
            </button>
          </div>

          {result && (
            <div className={`mt-4 px-4 py-3 rounded-lg text-sm font-medium ${
              result.success
                ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20"
            }`}>
              {result.message}
            </div>
          )}
        </div>

        {/* Clear Server Cache */}
        <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Clear Server Cache</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Clears the server-side metadata cache (vendors, inventory items, locations, financial periods). The cache refreshes automatically, but use this if you need changes to take effect immediately without running a full sync.
              </p>
            </div>
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <DatabaseZap size={16} className={clearingCache ? "animate-pulse" : ""} />
              {clearingCache ? "Clearing..." : "Clear Cache"}
            </button>
          </div>

          {cacheResult && (
            <div className={`mt-4 px-4 py-3 rounded-lg text-sm font-medium ${
              cacheResult.success
                ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20"
            }`}>
              {cacheResult.message}
            </div>
          )}
        </div>

        <NotificationEmailsAdmin />

        {/* Batch Color Coding */}
        <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Batch Color Coding</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Configure the color tags users can assign to batches. Each tag has a color, optional icon, and label shown on hover. Disabling a color hides it from users (existing batches keep their assigned color).
          </p>

          {colorsLoading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-2">
              {colors.map((color) => (
                <div
                  key={color.colorValue}
                  className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 dark:border-slate-800"
                >
                  {/* Color swatch + icon preview */}
                  <div
                    className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: color.hexColor || "#94a3b8" }}
                  >
                    {color.icon && (
                      <DynamicIcon name={color.icon} size={14} className="text-white" />
                    )}
                  </div>

                  {/* Hex color picker */}
                  <input
                    type="color"
                    value={color.hexColor || "#94a3b8"}
                    onChange={(e) => updateLocalColor(color.colorValue, { hexColor: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer border-0 p-0 shrink-0"
                    title="Pick color"
                  />

                  {/* Icon picker */}
                  <IconPicker
                    value={color.icon}
                    onChange={(icon) => updateLocalColor(color.colorValue, { icon })}
                  />

                  {/* Label */}
                  <input
                    type="text"
                    value={color.label}
                    onChange={(e) => updateLocalColor(color.colorValue, { label: e.target.value })}
                    placeholder="Label"
                    className="flex-1 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
                  />

                  {/* Enabled toggle */}
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 select-none shrink-0">
                    <input
                      type="checkbox"
                      checked={color.isEnabled}
                      onChange={(e) => updateLocalColor(color.colorValue, { isEnabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Enabled
                  </label>

                  {/* Save */}
                  <button
                    onClick={() => saveColor(color)}
                    disabled={savingColor === color.colorValue}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-white font-semibold rounded-md disabled:opacity-50 shrink-0"
                  >
                    <Save size={14} />
                    {savingColor === color.colorValue ? "..." : "Save"}
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => handleDeleteColor(color.colorValue)}
                    className="p-1.5 text-slate-400 hover:text-rose-500 shrink-0"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new color */}
          <div className="mt-4 flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700">
            <div
              className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center"
              style={{ backgroundColor: newHex }}
            >
              {newIcon && <DynamicIcon name={newIcon} size={14} className="text-white" />}
            </div>
            <input
              type="color"
              value={newHex}
              onChange={(e) => setNewHex(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border-0 p-0 shrink-0"
              title="Pick color"
            />
            <IconPicker value={newIcon} onChange={setNewIcon} />
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="New tag label..."
              className="flex-1 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
            />
            <button
              onClick={handleCreateColor}
              disabled={creating || !newLabel.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-md disabled:opacity-50 shrink-0"
            >
              <Plus size={14} />
              {creating ? "Adding..." : "Add"}
            </button>
          </div>

          {colorSaveMsg && (
            <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">{colorSaveMsg}</div>
          )}
        </div>

        {/* Entities */}
        <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Entities</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Entities group branches together and act as a hard cap on a user's visibility. A user assigned to an entity can only see records whose branch belongs to that entity, regardless of role grants.
          </p>

          <div className="space-y-3 mb-4">
            <input
              type="text"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="Entity name"
              className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
            />
            <input
              type="text"
              value={entityBranchesText}
              onChange={(e) => setEntityBranchesText(e.target.value)}
              placeholder="Branches (comma separated)"
              className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
            />
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Reconciliation Methods</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {([
                  ["Material Count", entityUseMaterialCount, setEntityUseMaterialCount],
                  ["Material Qty",   entityUseMaterialQty,   setEntityUseMaterialQty],
                  ["Supplier",       entityUseSupplier,      setEntityUseSupplier],
                  ["Hauler",         entityUseHauler,        setEntityUseHauler],
                ] as [string, boolean, (v: boolean) => void][]).map(([label, val, set]) => (
                  <label key={label} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                    <input type="checkbox" checked={val} onChange={e => set(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer" />
                    {label}
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none pt-1">
                <input type="checkbox" checked={entityUseBatchPageV2} onChange={e => setEntityUseBatchPageV2(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer" />
                Use Batch Page V2
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveEntity}
                disabled={!entityName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold rounded-md"
              >
                <Save size={14} />
                {entityEditingId == null ? "Add Entity" : "Update Entity"}
              </button>
              {entityEditingId != null && (
                <button
                  onClick={resetEntityForm}
                  className="px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-100 font-semibold rounded-md"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {entitiesLoading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : entities.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No entities yet.</div>
          ) : (
            <div className="space-y-2">
              {entities.map((ent) => (
                <div
                  key={ent.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">{ent.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {ent.branches.length === 0 ? "(no branches)" : ent.branches.join(", ")}
                    </div>
                  </div>
                  <button
                    onClick={() => startEditEntity(ent)}
                    className="p-1.5 text-slate-500 hover:text-primary"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => removeEntity(ent.id)}
                    className="p-1.5 text-slate-500 hover:text-rose-500"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {entityMsg && (
            <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">{entityMsg}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminToolsPage;
